import { Box } from '@mantine/core';
import PageLoader from '../../UI/loader/PageLoader';
import ProtectionPoliciesContentNew from './protection-policies/ProtectionPoliciesContentNew';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import NetWorthAccordion from './net-worth/NetWorthAccordion';
import useFinancialItems from 'src/financial/financial-data/hooks/useFinancialItems';
import { useNavigate, useParams } from 'react-router';
import { CreateFinancialItemPicker } from 'src/financial/financial-data/financial-item/CreateFinancialItemDrawer';
import { FinancialContentRework } from 'src/financial/financial-data/rework/FinancialContentRework';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useEffect } from 'react';
import { useAppContainerContext } from 'src/UI/app-container/AppContainerContext';
import { DataLoadError } from 'src/common/components/data-states/DataLoadError';

export const FinancialContent = () => {
  const { clientId, client } = useCurrentClient();
  const { sectionSlug } = useParams();
  const navigate = useNavigate();
  const { setSidebarCollapsed } = useAppContainerContext();
  const { flagEnabledFinancialRework } = useFlags();

  const isCreateView = sectionSlug === 'create';

  useEffect(() => {
    if (isCreateView) {
      setSidebarCollapsed(true);
      return () => setSidebarCollapsed(false);
    }
  }, [isCreateView, setSidebarCollapsed]);

  const {
    isLoading: loadingFinancialItems,
    isError: financialItemsErrored,
    isLoadingError: financialItemsFailed,
    refetch: refetchFinancialItems,
    isFetching,
    ...financialItems
  } = useFinancialItems(clientId);

  const refetch = async () => {
    await Promise.all([refetchFinancialItems()]);
  };

  if (loadingFinancialItems || !client) {
    return <PageLoader absolute={false} />;
  }

  if (sectionSlug === 'create') {
    return (
      <CreateFinancialItemPicker onClose={() => navigate(`/client/${clientId}/financial-data`)} />
    );
  }

  // A read that never loaded is not a client with nothing recorded. Showing the sections now
  // would total an empty list to zero, which reads as "nothing here" and invites re-entering
  // data that is already recorded. A refetch that fails over items already on screen is reported
  // above them instead: replacing the page under the adviser would unmount whatever form they have
  // open.
  if (financialItemsFailed) {
    return (
      <DataLoadError
        title="Financial data could not be loaded"
        subject={`${client.firstName}'s`}
        what="financial data"
        onRetry={refetch}
        testId="financial-load-error"
      />
    );
  }

  const refetchFailure = financialItemsErrored && (
    <Box mb={24}>
      <DataLoadError
        title="Financial data could not be refreshed"
        subject={`${client.firstName}'s latest`}
        what="financial data"
        onRetry={refetch}
        testId="financial-refetch-error"
      />
    </Box>
  );

  if (flagEnabledFinancialRework && clientId) {
    return (
      <>
        {refetchFailure}
        <FinancialContentRework
          clientId={clientId}
          clientFirstName={client.firstName}
          financialItems={financialItems}
        />
      </>
    );
  }

  return (
    <>
      {refetchFailure}
      <NetWorthAccordion
        onSubmit={refetch}
        financialItems={financialItems}
        isFetching={isFetching}
      />
      <ProtectionPoliciesContentNew
        policies={financialItems.protections}
        clientFirstName={client.firstName}
      />
    </>
  );
};
